import React from "react";
import { T, STATUS } from "../theme/tokens.js";
import { Panel, Empty } from "./ui/index.jsx";
import { vendorRollup } from "../lib/calc.js";

/* The owner's Dashboard tab, computed rather than maintained.
   Overdue CD is the number that matters most, so it is the one that shouts. */
export default function VendorRollup({ deliverables, onOpen }) {
  const { rows, total } = vendorRollup(deliverables);

  if (!rows.length) {
    return (
      <Panel title="Vendor deliverable dashboard" accent={T.gold}>
        <Empty text="No deliverables yet, so there is nothing to roll up." />
      </Panel>
    );
  }

  const head = ["Vendor", "Total", "Complete", "In Progress", "Not Started", "Overdue", "Overdue CD", "% Complete"];

  /* Each figure opens the deliverables it counted. A count you cannot open is
     a count somebody has to go and reconstruct by hand. */
  const Cell = ({ r, bucket, name, label, style }) => {
    const records = r.records?.[bucket] ?? [];
    const can = onOpen && records.length > 0;
    return (
      <td
        onClick={can ? () => onOpen(`${name}: ${label}`, records) : undefined}
        title={can ? `Open these ${records.length}` : undefined}
        style={{ padding: "6px 9px", textAlign: "center",
                 cursor: can ? "pointer" : "default", ...style }}
      >{r[bucket]}</td>
    );
  };

  const Row = ({ name, r, bold }) => (
    <tr style={{
      borderBottom: `1px solid ${T.line}`,
      fontWeight: bold ? 700 : 400,
      background: bold ? T.chip : "transparent",
    }}>
      <td
        onClick={onOpen && r.records?.total?.length
          ? () => onOpen(`${name}: all deliverables`, r.records.total) : undefined}
        style={{ padding: "6px 9px", cursor: onOpen ? "pointer" : "default" }}
      >{name}</td>
      <Cell r={r} bucket="total" name={name} label="all deliverables" />
      <Cell r={r} bucket="complete" name={name} label="complete"
            style={{ color: STATUS.complete.ink }} />
      <Cell r={r} bucket="inProgress" name={name} label="in progress"
            style={{ color: STATUS.inProgress.ink }} />
      <Cell r={r} bucket="notStarted" name={name} label="not started"
            style={{ color: STATUS.notStarted.ink }} />
      <Cell r={r} bucket="overdue" name={name} label="overdue"
            style={{ color: r.overdue ? T.risk : T.soft,
                     fontWeight: r.overdue ? 700 : 400 }} />
      <Cell r={r} bucket="overdueContractDeliverables" name={name}
            label="overdue contract deliverables"
            style={{ color: r.overdueContractDeliverables ? T.risk : T.soft,
                     fontWeight: r.overdueContractDeliverables ? 700 : 400 }} />
      <td style={{ padding: "6px 9px", textAlign: "center" }}>
        {r.percentComplete === null ? "-" : `${r.percentComplete}%`}
      </td>
    </tr>
  );

  return (
    <Panel title="Vendor deliverable dashboard" accent={T.gold}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>{head.map((h) => (
              <th key={h} style={{
                textAlign: h === "Vendor" ? "left" : "center", padding: "6px 9px",
                borderBottom: `2px solid ${T.navy}`, color: T.navy, fontSize: 11,
                fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
              }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r) => <Row key={r.vendor} name={r.vendor} r={r} />)}
            <Row name="PROGRAM TOTAL" r={total} bold />
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 12, color: T.soft, marginTop: 8 }}>
        Overdue counts anything not complete whose date has passed, using the contractual
        date or the schedule of record when there is no contractual one. Overdue CD narrows
        that to contract deliverables, the ones that matter most.
        {onOpen ? " Click any figure to open the deliverables behind it." : ""}
      </div>
    </Panel>
  );
}
